/**
 * 数字が主役ではない取組のための「誰が何をやっていて、進捗はどうか」ボード。
 *
 * 中身（取組名・担当・判断待ちの文言）は deploy 供給
 * （/api/meeting-board/progress）が持ち、ここには器だけを置く。
 *
 * 進捗バーの見た目は BoardMetricGrid（数字タブ）と揃えて、タブ間で読み方が変わらないようにする。
 */
import { useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, CircleDot } from 'lucide-react'

import { Badge } from '@/components/Badge'
import { Card, CardContent } from '@/components/Card'
import { useBoardDocument, useBoardDocuments, useBoardSection } from '@/hooks/useMeetingBoard'
import { BoardReportViewer, type BoardReportType } from '@/components/BoardReportViewer'
import { cn } from '@/lib/utils'

interface Stream { title: string; owner: string | null; note: string }
interface ProgressRow {
  label: string
  owner: string | null
  pct: number
  /** wip=進行中 / blocked=判断待ちで止まっている / done=運用中 */
  state: 'wip' | 'blocked' | 'done'
  note: string
}
interface DecisionRow { title: string; askedOf: string | null; detail: string; state: string }
interface NextAction { text: string; due: string | null; owner: string | null }

export interface ProgressBoard {
  tab: string
  asOf: string
  sourceLabel: string
  reportType: BoardReportType | null
  reportDate: string | null
  /**
   * 資料セクションの表記と鮮度しきい値。すべて任意で、未指定なら週次資料の既定値になる。
   * 週次と日次で「何が古いか」も「何と呼ぶか」も違うため、コードに固定せず deploy 供給にする。
   */
  reportLabel?: string | null
  reportDescription?: string | null
  reportOpenNote?: string | null
  reportUnitLabel?: string | null
  reportStaleAfterDays?: number | null
  /** 資料がそのタブの主役のとき（日報など）に、最初から開いた状態にする */
  reportDefaultOpen?: boolean | null
  periodLabel: string | null
  coverage: { owners: string[]; complete: boolean; note: string }
  streams: Stream[]
  progress: ProgressRow[]
  decisions: DecisionRow[]
  decisionsNote: string | null
  nextActions: NextAction[]
}

/** 表記を書いていない既存タブの見え方を変えないための既定値（週次資料） */
const REPORT_DEFAULTS = {
  label: '週次資料',
  description: '毎週まとめている進捗資料をそのまま表示します。',
  openNote: '今週やったことの詳細・移行試算ほか',
  unitLabel: '分',
  staleAfterDays: 8,
} as const

export function useProgressBoard(tab: string): ProgressBoard | undefined {
  return useBoardSection<ProgressBoard>('progress', tab).data ?? undefined
}

/** 目次には本文を載せない。本文は開いたときに 1 件だけ取りに行く。 */
interface DocItem { key: string; title: string; note: string }
interface DocList { tab: string; items: DocItem[] }

function useDocuments(tab: string): DocList | undefined {
  return useBoardDocuments<DocList>(tab).data
}

/**
 * 資料を畳んだ状態で並べ、開いた分だけ iframe で表示する。
 *
 * `sandbox="allow-scripts"` のみ（`allow-same-origin` は付けない）。
 * 資料側のスクリプト（シミュレーターの計算）は動くが、親の画面や cookie には触れない。
 */
function BoardDocumentList({ docs }: { docs: DocList }) {
  const [open, setOpen] = useState<string | null>(null)
  const openDocument = useBoardDocument(open)
  return (
    <Card>
      <div className="border-b px-4 py-3 md:px-6">
        <h3 className="font-semibold">資料</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">開くとこの画面の中に表示します</p>
      </div>
      <ul className="divide-y">
        {docs.items.map((doc) => {
          const isOpen = open === doc.key
          return (
            <li key={doc.key}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : doc.key)}
                aria-expanded={isOpen}
                className="flex min-h-11 w-full items-start gap-2 px-4 py-3 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:px-6"
              >
                {isOpen
                  ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />}
                <span className="min-w-0">
                  <span className="block font-medium">{doc.title}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{doc.note}</span>
                </span>
              </button>
              {isOpen && (
                <div className="border-t bg-muted/30 p-2 md:p-4">
                  {openDocument.isLoading ? (
                    <p className="px-2 py-6 text-sm text-muted-foreground">読み込み中…</p>
                  ) : openDocument.data ? (
                    <iframe
                      title={doc.title}
                      srcDoc={openDocument.data.html}
                      sandbox="allow-scripts"
                      className="h-[70vh] w-full rounded-md border bg-white"
                    />
                  ) : (
                    <p className="px-2 py-6 text-sm text-muted-foreground">
                      この資料は取得できませんでした。
                    </p>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </Card>
  )
}

/** 進捗バー 1 本。色は「止まっている＝赤 / 運用中＝緑 / 進行中＝青」。 */
function ProgressRowView({ row }: { row: ProgressRow }) {
  const tone = row.state === 'blocked' ? 'bad' : row.state === 'done' ? 'good' : 'wip'
  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex min-w-0 flex-wrap items-baseline gap-2">
          <span className="font-medium">{row.label}</span>
          {row.owner && <span className="text-xs text-muted-foreground">担当 {row.owner}</span>}
          {row.state === 'blocked' && <Badge tone="destructive" className="rounded-md">判断待ちで停止</Badge>}
          {row.state === 'done' && <Badge tone="success" className="rounded-md">運用中</Badge>}
        </div>
        <span className={cn(
          'text-xl font-bold tabular-nums',
          tone === 'bad' ? 'text-red-600' : tone === 'good' ? 'text-emerald-600' : 'text-foreground',
        )}>{row.pct}%</span>
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full', tone === 'bad' ? 'bg-red-600' : tone === 'good' ? 'bg-emerald-600' : 'bg-primary')}
          style={{ width: `${Math.max(0, Math.min(100, row.pct))}%` }}
        />
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">{row.note}</p>
    </div>
  )
}

/**
 * 資料（全文）の開閉セクション。
 *
 * 表記と鮮度しきい値は deploy 供給で、書いていなければ週次資料の既定値を使う。
 * reportType は呼び出し側で null を除いてから渡す（ここで再度の絞り込みをしない）。
 */
function ReportSection({ board, reportType }: { board: ProgressBoard; reportType: BoardReportType }) {
  // null = まだ触っていない。その間は配信側の既定に従う。
  const [toggled, setToggled] = useState<boolean | null>(null)
  const open = toggled ?? board.reportDefaultOpen === true
  const label = board.reportLabel ?? REPORT_DEFAULTS.label

  return (
    <Card>
      <button
        type="button"
        onClick={() => setToggled(!open)}
        aria-expanded={open}
        className="flex min-h-11 w-full items-center gap-1 px-4 py-3 text-left text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:px-6"
      >
        {open ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
        {label}（全文）を{open ? '閉じる' : '開く'}
        <span className="ml-2 text-xs font-normal text-muted-foreground">
          {board.reportOpenNote ?? REPORT_DEFAULTS.openNote}
        </span>
      </button>
      {open && (
        <CardContent className="border-t p-4 md:p-6">
          <BoardReportViewer
            reportType={reportType}
            title={label}
            description={board.reportDescription ?? REPORT_DEFAULTS.description}
            dateUnitLabel={board.reportUnitLabel ?? REPORT_DEFAULTS.unitLabel}
            staleAfterDays={board.reportStaleAfterDays ?? REPORT_DEFAULTS.staleAfterDays}
          />
        </CardContent>
      )}
    </Card>
  )
}

export function BoardProgressBoard({ tab }: { tab: string }) {
  const board = useProgressBoard(tab)
  const documents = useDocuments(tab)
  const hasDocuments = (documents?.items.length ?? 0) > 0
  if (!board && !hasDocuments) return null

  return (
    <div className="space-y-4">
      {/* 誰の分が入っているかを最初に断る（他メンバー分が無いことが一目で分かるように） */}
      {board && !board.coverage.complete && (
        <div className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-700" aria-hidden />
          <p className="text-sm text-yellow-800">{board.coverage.note}</p>
        </div>
      )}

      {/* 0 件のときは見出しだけのカードになるので、そもそも出さない */}
      {documents && documents.items.length > 0 && <BoardDocumentList docs={documents} />}

      {board && board.streams.length > 0 && (
      <Card>
        <div className="flex flex-col gap-1 border-b px-4 py-3 md:flex-row md:items-baseline md:justify-between md:px-6">
          <h3 className="font-semibold">いま動いていること</h3>
          <span className="text-xs tabular-nums text-muted-foreground">
            {board.sourceLabel}・{board.asOf} 時点
            {board.periodLabel && `（${board.periodLabel}）`}
          </span>
        </div>
        <ul className="divide-y">
          {board.streams.map((s) => (
            <li key={s.title} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3 md:px-6">
              <CircleDot className="h-4 w-4 shrink-0 self-center text-primary" aria-hidden />
              <span className="font-medium">{s.title}</span>
              {s.owner && <span className="text-xs text-muted-foreground">担当 {s.owner}</span>}
              <span className="w-full text-sm text-muted-foreground md:w-auto md:flex-1">{s.note}</span>
            </li>
          ))}
        </ul>
      </Card>
      )}

      {board && board.decisions.length > 0 && (
        <Card className="border-yellow-300">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-yellow-300 bg-yellow-50 px-4 py-3 md:px-6">
            <h3 className="font-semibold text-yellow-900">判断してほしいこと</h3>
            <Badge tone="warning" className="rounded-md tabular-nums">{board.decisions.length}件</Badge>
          </div>
          <ul className="divide-y">
            {board.decisions.map((d) => (
              <li key={d.title} className="px-4 py-3 md:px-6">
                <p className="font-medium">{d.title}</p>
                <p className="mt-1 text-sm text-muted-foreground">{d.detail}</p>
              </li>
            ))}
          </ul>
          {board.decisionsNote && (
            <p className="border-t px-4 py-2 text-xs text-muted-foreground md:px-6">{board.decisionsNote}</p>
          )}
        </Card>
      )}

      {board && board.progress.length > 0 && (
        <Card>
          <div className="border-b px-4 py-3 md:px-6">
            <h3 className="font-semibold">それぞれの進捗</h3>
          </div>
          <div className="divide-y">
            {board.progress.map((row) => <ProgressRowView key={row.label} row={row} />)}
          </div>
        </Card>
      )}

      {board && board.nextActions.length > 0 && (
        <Card>
          <div className="border-b px-4 py-3 md:px-6">
            <h3 className="font-semibold">この後やること</h3>
          </div>
          <ul className="divide-y">
            {board.nextActions.map((a) => (
              <li key={a.text} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3 md:px-6">
                <span className="min-w-0 flex-1 text-sm">{a.text}</span>
                {a.owner && <span className="text-xs text-muted-foreground">担当 {a.owner}</span>}
                {a.due && <Badge tone="muted" className="rounded-md">{a.due}</Badge>}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {board?.reportType && <ReportSection board={board} reportType={board.reportType} />}
    </div>
  )
}
